/**
 * angular-admin-oskey-io
 *
 * @license
 * Copyright (c) 2021-2025, OSKEY SAS. All rights reserved.
 *
 * The source code is protected under international copyright law.
 * All rights reserved and protected by the copyright holders.
 *
 * The source code is confidential and only available to authorized individuals with the permission of the copyright holders.
 * If you encounter this source code and do not have permission, please contact the copyright holders and delete this file.
 */

import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Signal,
  WritableSignal,
  inject,
  signal
} from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterModule } from '@angular/router';
import { OSKAuthService } from '../../features/authentication/services/auth.service';
import { OSKCurrentUser, OSKUserAccount } from '@oskey/core/types';
import { OSKTranslatePipe } from '@oskey/translate';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OSKCurrentUserToken } from '@oskey/core';
import {
  OSKAvatarComponent,
  OSKButtonComponent,
  OSKIconComponent,
  OSKLogoComponent
} from '@oskey/ui';

@Component({
  selector: 'osk-header',
  standalone: true,
  imports: [
    RouterModule,
    OSKTranslatePipe,
    OSKLogoComponent,
    OSKAvatarComponent,
    OSKButtonComponent,
    OSKIconComponent
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OSKHeaderComponent {
  protected readonly currentUser: Signal<OSKCurrentUser> = inject(OSKCurrentUserToken);
  private readonly authService: OSKAuthService = inject(OSKAuthService);
  private readonly router: Router = inject(Router);
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);

  protected selectedAccount: WritableSignal<OSKUserAccount | undefined> = signal(undefined);
  protected accounts: WritableSignal<OSKUserAccount[]> = signal([]);
  protected currentUrl: WritableSignal<string> = signal('/');
  protected isMenuOpen: WritableSignal<boolean> = signal(false);

  private initialNavigation: boolean = true;
  constructor() {
    this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (this.initialNavigation && event instanceof NavigationStart) {
        this.currentUrl.set(event.url);
        this.initialNavigation = false;
      }
      if (event instanceof NavigationEnd) {
        this.currentUrl.set(event.urlAfterRedirects);
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      this.isMenuOpen() &&
      event.target instanceof Node &&
      !this.elementRef.nativeElement.contains(event.target)
    ) {
      this.isMenuOpen.set(false);
    }
  }

  toggleMenu(event: Event): void {
    event.stopPropagation();
    this.isMenuOpen.update((open) => !open);
  }

  get userInitials(): string {
    const profile = this.currentUser().oskUser?.publicProfile;
    if (profile?.firstName && profile?.lastName) {
      return `${profile.firstName.charAt(0)}${profile.lastName.charAt(0)}`.toUpperCase();
    }
    if (profile?.firstName) {
      return profile.firstName.substring(0, 2).toUpperCase();
    }
    const email = this.currentUser().firebaseUser?.email;
    if (email) {
      return email.substring(0, 2).toUpperCase();
    }
    return 'OS';
  }

  signOut(): void {
    this.isMenuOpen.set(false);
    this.authService.signOut().then(() => {
      this.router.navigateByUrl('/');
    });
  }
}
